import { useBackgroundQuery, useReadQuery } from '@apollo/client';

import { UnauthorizedJobReason } from '@xing-com/crate-common-graphql-types';
import { useParams } from '@xing-com/crate-core-router';
import { useLoginState } from '@xing-com/crate-hooks-use-login-state';
import { JobNotAvailable } from '@xing-com/crate-jobs-domain-job-details-components-error';
import { GetUserJobPreferencesDocument } from '@xing-com/crate-jobs-graphql';
import { selectJobDetails } from '@xing-com/crate-jobs-helpers';
import type { JobDetails as JobDetailsType } from '@xing-com/crate-jobs-helpers';
import {
  useJobDetails,
  useJobMatchingSkills,
  getPreferencesData,
} from '@xing-com/crate-jobs-hooks';
import { NoSSRLoggedInSuspense } from '@xing-com/crate-utils-no-ssr';
import { useHost } from '@xing-com/crate-xinglet';

import { JobDetailsContent } from './job-details-content';
import { JobDetailsLoading } from './job-details-loading';

const JobDetailsWithData: React.FC<{ jobDetails: JobDetailsType }> = ({
  jobDetails,
}) => {
  const { slug } = useParams<{ slug: string }>();

  const [queryRef] = useBackgroundQuery(GetUserJobPreferencesDocument);
  const { matchedSkills, notMatchedSkills } = useJobMatchingSkills({
    slug,
  });

  const { data } = useReadQuery(queryRef);
  const userJobPreferences = getPreferencesData(data);

  return (
    <JobDetailsContent
      jobDetails={jobDetails}
      userJobPreferences={userJobPreferences}
      matchedSkills={matchedSkills}
      notMatchedSkills={notMatchedSkills}
    />
  );
};

const JobDetailsInner: React.FC = () => {
  const host = useHost();
  const { slug } = useParams<{ slug: string }>();
  const { job, error } = useJobDetails(slug);
  const { isLoggedIn } = useLoginState();

  if (!job) {
    if (error === UnauthorizedJobReason.Private && !isLoggedIn) {
      return host.redirectToLogin();
    } else {
      return <JobNotAvailable />;
    }
  }

  const displaySkillsAndPreferences =
    isLoggedIn && job && !job.redirectsToThirdPartyUrl;

  const jobDetails = selectJobDetails(job);

  const Component = displaySkillsAndPreferences
    ? JobDetailsWithData
    : JobDetailsContent;

  return <Component jobDetails={jobDetails} />;
};

export const JobDetails: React.FC = () => {
  return (
    <NoSSRLoggedInSuspense fallback={<JobDetailsLoading />}>
      <JobDetailsInner />
    </NoSSRLoggedInSuspense>
  );
};
